/**
 * Computes all section totals from sub-scores so numbers are always
 * arithmetically consistent across every card in the assessment UI.
 *
 * Every card should use these computed values instead of the LLM-provided
 * totals in `scores.*`, which may contain arithmetic errors.
 */
import { AssessmentResult } from '../../types/assessment';

export interface ComputedScores {
  // Quality
  editorialTotal: number;
  authenticityTotal: number;
  qualityTotal: number;

  // Veracity
  sourceId: number;
  sourceQuality: number;
  sourceFraming: number;
  lateralVerification: number;
  sourceAccessibility: number;
  veracityArticle: number;
  veracityFinal: number;

  // Valence
  valenceSubtotal50: number;
  valenceAdjusted50: number;
  valenceTotal: number;

  // Manipulation
  manipulationTotal: number;

  // Weighted final
  accuracy: number;
  emotional: number;
  accuracyWeighted: number;
  emotionalWeighted: number;
  baseScore: number;
  finalScore: number;
}

export function computeScores(result: AssessmentResult): ComputedScores {
  const { scores, is_opinion, quality_breakdown: qb, veracity_breakdown: vb, valence_breakdown: vlb, manipulation_breakdown: mb } = result;

  // ── Quality ──
  const editorialTotal = (qb.editorial_presentation ?? 0) + (qb.editorial_corrections ?? 0) + (qb.editorial_temporal ?? 0);
  const authenticityTotal = (qb.authenticity_ai_disclosure ?? 0) + (qb.authenticity_provenance ?? 0);
  const qualityTotal = is_opinion ? 0 :
    Math.max(0, (qb.completeness ?? 0) + (qb.balance ?? 0) + (qb.depth ?? 0) + editorialTotal + (qb.transparency ?? 0) + authenticityTotal - qb.temporal_penalty);

  // ── Veracity ──
  const sourceId = vb.named_sources + vb.attribution_clarity;
  const sourceQuality = vb.primary_sources + vb.expert_sources + vb.source_independence;
  const sourceFraming = vb.context_provided + vb.appropriate_skepticism + vb.conflicting_sources;
  const lateralVerification = vb.multiple_confirmation + vb.cross_reference;
  const sourceAccessibility = vb.links + vb.retrievability;
  const veracityArticle = sourceId + sourceQuality + sourceFraming + lateralVerification + sourceAccessibility;
  const veracityRaw = veracityArticle + scores.veracity_adjustment;
  const veracityFinal = Math.round((veracityRaw / 110) * 100);

  // ── Valence ──
  const valenceSubtotal50 = (vlb.intensity_pts ?? 0) + (vlb.valence_type_pts ?? 0) + (vlb.cumulative_load_pts ?? 0) + (vlb.amplifiers_pts ?? 0);
  const valenceAdjusted50 = Math.min(50, valenceSubtotal50 + (vlb.article_type_adjustment ?? 0));
  const valenceTotal = is_opinion ? 0 : valenceAdjusted50 * 2;

  // ── Manipulation ──
  const manipulationTotal = 100 - Math.abs(mb.loaded_language) - Math.abs(mb.logical_fallacies) - Math.abs(mb.diversion) - Math.abs(mb.social_pressure) - Math.abs(mb.cognitive_bias) - Math.abs(mb.dehumanization);

  // ── Weighted final ──
  const accuracy = is_opinion ? 0 : (qualityTotal + veracityFinal) / 2;
  const emotional = is_opinion ? 0 : (valenceTotal + manipulationTotal) / 2;
  const accuracyWeighted = accuracy * 0.6;
  const emotionalWeighted = emotional * 0.4;
  const baseScore = accuracyWeighted + emotionalWeighted;
  const finalScore = is_opinion
    ? Math.round((veracityFinal + manipulationTotal) / 2)
    : Math.min(100, Math.max(0, Math.round(baseScore)));

  return {
    editorialTotal, authenticityTotal, qualityTotal,
    sourceId, sourceQuality, sourceFraming, lateralVerification, sourceAccessibility, veracityArticle, veracityFinal,
    valenceSubtotal50, valenceAdjusted50, valenceTotal,
    manipulationTotal,
    accuracy, emotional, accuracyWeighted, emotionalWeighted, baseScore, finalScore,
  };
}
